package dev.danko.roblox_craft_ai.queue

import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.snapshots.SnapshotStateList
import dev.danko.roblox_craft_ai.shared.model.AssetType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.js.Date
import kotlin.random.Random

enum class QueuedStatus {
    PENDING,
    ACTIVE,
    COMPLETED,
    FAILED,
    CANCELLED
}

data class QueuedAsset(
    val uuid: String,
    val prompt: String,
    val type: AssetType,
    val providerId: String? = null,
    val status: QueuedStatus = QueuedStatus.PENDING,
    val position: Int = 0,
    val priority: Int = 0,
    val progressPct: Int = 0,
    val startedAtUtc: Long? = null,
    val completedAtUtc: Long? = null,
    val etaSeconds: Long? = null,
    val errorMessage: String? = null,
    val resultAssetId: String? = null
)

private fun generateUuid(): String {
    val chars = "abcdef0123456789"
    return buildString {
        repeat(16) { append(chars[Random.nextInt(chars.length)]) }
    }
}

object GenerateQueueScheduler {
    const val MAX_CONCURRENT = 3
    const val TICK_MS = 500L
    const val DEFAULT_ETA_SEC = 30L

    val queue: SnapshotStateList<QueuedAsset> = mutableStateListOf()
    var isPaused: Boolean = false

    var onStartItem: (suspend (QueuedAsset) -> Result<String>)? = null

    private val activeJobs: MutableMap<String, Job> = mutableMapOf()
    private val recentDurations: ArrayDeque<Long> = ArrayDeque()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val positionComparator = compareByDescending<QueuedAsset> { it.priority }.thenBy { it.position }

    init {
        startSchedulerLoop()
    }

    fun enqueue(prompt: String, type: AssetType, providerId: String? = null, priority: Int = 0): QueuedAsset {
        val uuid = generateUuid()
        val position = (queue.filter { it.status == QueuedStatus.PENDING }.maxOfOrNull { it.position } ?: 0) + 1
        val asset = QueuedAsset(
            uuid = uuid,
            prompt = prompt,
            type = type,
            providerId = providerId,
            status = QueuedStatus.PENDING,
            position = position,
            priority = priority.coerceIn(-10, 10),
            etaSeconds = averageCompletedSeconds()
        )
        queue.add(asset)
        recalcPositions()
        return asset
    }

    fun cancel(uuid: String): Boolean {
        val idx = queue.indexOfFirst { it.uuid == uuid }
        if (idx < 0) return false
        val asset = queue[idx]
        if (asset.status == QueuedStatus.COMPLETED || asset.status == QueuedStatus.FAILED) return false

        if (asset.status == QueuedStatus.ACTIVE) {
            activeJobs[uuid]?.cancel()
            activeJobs.remove(uuid)
        }

        queue[idx] = asset.copy(
            status = QueuedStatus.CANCELLED,
            completedAtUtc = Date.now().toLong()
        )
        scheduleCleanup(uuid, 10_000L)
        recalcPositions()
        return true
    }

    fun pause() {
        isPaused = true
    }

    fun resume() {
        isPaused = false
    }

    fun changePriority(uuid: String, delta: Int) {
        val idx = queue.indexOfFirst { it.uuid == uuid }
        if (idx < 0) return
        val asset = queue[idx]
        if (asset.status != QueuedStatus.PENDING) return
        queue[idx] = asset.copy(priority = (asset.priority + delta).coerceIn(-10, 10))
        recalcPositions()
    }

    fun updateProgress(uuid: String, pct: Int) {
        val idx = queue.indexOfFirst { it.uuid == uuid }
        if (idx < 0) return
        val asset = queue[idx]
        val elapsed = asset.startedAtUtc?.let { (Date.now().toLong() - it) / 1000L }
        val eta = if (pct > 0 && elapsed != null && elapsed > 0) {
            (elapsed * (100 - pct) / pct.coerceAtLeast(1)).coerceAtLeast(1L)
        } else {
            asset.etaSeconds
        }
        queue[idx] = asset.copy(progressPct = pct.coerceIn(0, 100), etaSeconds = eta)
    }

    fun complete(uuid: String, resultAssetId: String?, error: String?) {
        val idx = queue.indexOfFirst { it.uuid == uuid }
        if (idx < 0) return
        val asset = queue[idx]
        val now = Date.now().toLong()
        val durationSec = asset.startedAtUtc?.let { ((now - it) / 1000L) }
        if (durationSec != null && durationSec > 0) {
            pushCompletedDuration(durationSec)
        }
        activeJobs.remove(uuid)
        val status = if (error == null) QueuedStatus.COMPLETED else QueuedStatus.FAILED
        queue[idx] = asset.copy(
            status = status,
            progressPct = if (error == null) 100 else asset.progressPct,
            completedAtUtc = now,
            resultAssetId = resultAssetId,
            errorMessage = error,
            etaSeconds = 0L
        )
        recalcPositions()
    }

    fun clearCompleted() {
        queue.removeAll {
            it.status == QueuedStatus.COMPLETED ||
                it.status == QueuedStatus.FAILED ||
                it.status == QueuedStatus.CANCELLED
        }
        recalcPositions()
    }

    fun cancelAll() {
        val snapshot = queue.toList()
        for (asset in snapshot) {
            when (asset.status) {
                QueuedStatus.PENDING -> cancel(asset.uuid)
                QueuedStatus.ACTIVE -> cancel(asset.uuid)
                else -> {}
            }
        }
    }

    fun calcEta(asset: QueuedAsset): Long? {
        val now = Date.now().toLong()
        return when (asset.status) {
            QueuedStatus.ACTIVE -> {
                val started = asset.startedAtUtc ?: return averageCompletedSeconds()
                val elapsedSec = ((now - started) / 1000L).coerceAtLeast(1L)
                if (asset.progressPct > 0) {
                    (elapsedSec * (100 - asset.progressPct) / asset.progressPct.coerceAtLeast(1)).coerceAtLeast(1L)
                } else {
                    averageCompletedSeconds()
                }
            }
            QueuedStatus.PENDING -> {
                val avg = averageCompletedSeconds()
                val activeCount = queue.count { it.status == QueuedStatus.ACTIVE }.coerceAtLeast(1)
                val beforeCount = queue
                    .filter { it.status == QueuedStatus.PENDING }
                    .sortedWith(positionComparator)
                    .indexOfFirst { it.uuid == asset.uuid }
                    .coerceAtLeast(0)
                val aheadSlots = (beforeCount / MAX_CONCURRENT.toDouble()).toLong()
                avg * (aheadSlots + 1)
            }
            else -> null
        }
    }

    fun globalEtaSeconds(): Long? {
        val pendings = queue.filter { it.status == QueuedStatus.PENDING }
        val actives = queue.filter { it.status == QueuedStatus.ACTIVE }
        if (pendings.isEmpty() && actives.isEmpty()) return null
        val activeRemaining = actives.mapNotNull { calcEta(it) }.maxOrNull() ?: 0L
        if (pendings.isEmpty()) return activeRemaining
        val avg = averageCompletedSeconds()
        val pendingBatchCount = ((pendings.size + MAX_CONCURRENT - 1) / MAX_CONCURRENT.toDouble()).toLong()
        return activeRemaining + (pendingBatchCount * avg)
    }

    fun countActive(): Int = queue.count { it.status == QueuedStatus.ACTIVE }
    fun countPendingOrActive(): Int = queue.count { it.status == QueuedStatus.ACTIVE || it.status == QueuedStatus.PENDING }

    private fun averageCompletedSeconds(): Long {
        if (recentDurations.isEmpty()) return DEFAULT_ETA_SEC
        return (recentDurations.average()).toLong().coerceAtLeast(1L)
    }

    private fun pushCompletedDuration(seconds: Long) {
        recentDurations.addLast(seconds)
        while (recentDurations.size > 5) recentDurations.removeFirst()
    }

    private fun recalcPositions() {
        val sortedPendings = queue
            .filter { it.status == QueuedStatus.PENDING }
            .sortedWith(positionComparator)
        var pos = 1
        for (p in sortedPendings) {
            val idx = queue.indexOfFirst { it.uuid == p.uuid }
            if (idx >= 0 && queue[idx].position != pos) {
                queue[idx] = queue[idx].copy(position = pos)
            }
            pos++
        }
    }

    private fun scheduleCleanup(uuid: String, delayMs: Long) {
        scope.launch {
            delay(delayMs)
            queue.removeAll { it.uuid == uuid }
        }
    }

    private fun startSchedulerLoop() {
        scope.launch {
            while (isActive) {
                delay(TICK_MS)
                try {
                    tick()
                } catch (_: Throwable) { }
            }
        }
    }

    private fun tick() {
        if (isPaused) return
        val activeCount = countActive()
        if (activeCount >= MAX_CONCURRENT) return
        val slots = MAX_CONCURRENT - activeCount
        if (slots <= 0) return

        val candidates = queue
            .filter { it.status == QueuedStatus.PENDING }
            .sortedWith(positionComparator)
            .take(slots)

        for (candidate in candidates) {
            startItem(candidate)
        }
    }

    private fun startItem(candidate: QueuedAsset) {
        val idx = queue.indexOfFirst { it.uuid == candidate.uuid }
        if (idx < 0) return
        val now = Date.now().toLong()
        queue[idx] = candidate.copy(
            status = QueuedStatus.ACTIVE,
            startedAtUtc = now,
            etaSeconds = averageCompletedSeconds()
        )
        val current = queue[idx]
        val callback = onStartItem
        if (callback == null) {
            complete(current.uuid, resultAssetId = null, error = Txt.queue.startCallbackMissing)
            return
        }
        val job = scope.launch {
            try {
                val result = callback(current)
                if (result.isSuccess) {
                    complete(current.uuid, result.getOrNull(), error = null)
                } else {
                    complete(current.uuid, resultAssetId = null, error = result.exceptionOrNull()?.message ?: Txt.queue.generationFailed)
                }
            } catch (t: Throwable) {
                if (t is kotlinx.coroutines.CancellationException) {
                    complete(current.uuid, resultAssetId = null, error = Txt.errors.cancelled)
                } else {
                    complete(current.uuid, resultAssetId = null, error = t.message ?: Txt.queue.unexpectedError)
                }
            }
        }
        activeJobs[current.uuid] = job
    }
}

fun formatEta(seconds: Long?): String {
    if (seconds == null || seconds <= 0) return "–"
    val mins = seconds / 60L
    val secs = seconds % 60L
    return if (mins > 0) Txt.common.minutesSeconds(mins.toInt(), secs.toInt()) else Txt.common.secondsShort(secs.toInt())
}

fun formatCompletedAgo(completedAtUtc: Long?): String {
    if (completedAtUtc == null) return "–"
    val diffSec = ((Date.now().toLong() - completedAtUtc) / 1000L).coerceAtLeast(0L)
    if (diffSec < 60) return "✓ ${Txt.common.agoSeconds(diffSec.toInt())}"
    val mins = diffSec / 60L
    return "✓ ${Txt.common.agoMinutes(mins.toInt())}"
}
